import React from 'react';
import { FORNECEDORES, ARLA, CART_IDS, EMB_ARLA_MAP, fmt, hasPE, hasAditivo, embs, getSaldo, getUreiaSaldos, arlaByUnit } from '../constants';

function LineChart({ series, height = 80, colors = ['#185FA5'], labels = [] }) {
  if (!series || !series[0] || series[0].length < 2) return null;
  const W = 500, H = height;
  const allVals = series.flat();
  const min = Math.min(...allVals);
  const max = Math.max(...allVals);
  const range = max - min || 1;
  const pad = { t: 8, b: 20, l: 8, r: 8 };
  const w = W - pad.l - pad.r;
  const h = H - pad.t - pad.b;
  const n = series[0].length;
  const x = (i) => pad.l + (i / (n - 1)) * w;
  const y = (v) => pad.t + h - ((v - min) / range) * h;
  const path = (data) => data.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height }} preserveAspectRatio="none">
      <line x1={pad.l} y1={y(max)} x2={pad.l + w} y2={y(max)} stroke="#f0ede8" strokeWidth="0.5" />
      <line x1={pad.l} y1={y(min)} x2={pad.l + w} y2={y(min)} stroke="#f0ede8" strokeWidth="0.5" />
      {series.map((data, si) => (
        <g key={si}>
          <path d={path(data)} fill="none" stroke={colors[si] || '#185FA5'} strokeWidth="2" strokeLinejoin="round" />
          <circle cx={x(n - 1)} cy={y(data[n - 1])} r="3" fill={colors[si] || '#185FA5'} />
        </g>
      ))}
      {labels.map((l, i) => {
        if (i % Math.ceil(n / 6) !== 0 && i !== n - 1) return null;
        return <text key={i} x={x(i)} y={H - 4} textAnchor="middle" fontSize="9" fill="#aaa">{l}</text>;
      })}
    </svg>
  );
}

function BarChart({ data, height = 80, color = '#185FA5', labels = [] }) {
  if (!data || data.length < 2) return null;
  const W = 500, H = height;
  const max = Math.max(...data, 1);
  const pad = { t: 8, b: 20, l: 8, r: 8 };
  const w = W - pad.l - pad.r;
  const h = H - pad.t - pad.b;
  const n = data.length;
  const bw = Math.max(2, (w / n) * 0.7);
  const gap = w / n;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height }} preserveAspectRatio="none">
      {data.map((v, i) => {
        const bh = (v / max) * h;
        const bx = pad.l + i * gap + (gap - bw) / 2;
        const by = pad.t + h - bh;
        return <rect key={i} x={bx} y={by} width={bw} height={Math.max(1, bh)} fill={color} rx="1" opacity="0.85" />;
      })}
      {labels.map((l, i) => {
        if (i % Math.ceil(n / 6) !== 0 && i !== n - 1) return null;
        return <text key={i} x={pad.l + i * gap + gap / 2} y={H - 4} textAnchor="middle" fontSize="9" fill="#aaa">{l}</text>;
      })}
    </svg>
  );
}

function Gauge({ pct, label, color }) {
  const r = 28, cx = 40, cy = 36;
  const circ = 2 * Math.PI * r;
  const filled = (Math.min(100, pct) / 100) * circ;
  return (
    <div style={{ textAlign: 'center', minWidth: 80 }}>
      <svg width="80" height="50" style={{ overflow: 'visible' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f0ede8" strokeWidth="6" />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={`${filled} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`} />
        <text x={cx} y={cy + 5} textAnchor="middle" fontSize="11" fontWeight="700" fill={color}>{pct}%</text>
      </svg>
      <div style={{ fontSize: 11, color: '#888', marginTop: 2, fontFamily: '"DM Sans", sans-serif', lineHeight: 1.3 }}>{label}</div>
    </div>
  );
}

export default function Dashboard({ lancamentos, carteira, unit }) {
  const last = lancamentos[lancamentos.length - 1] || null;
  if (!last) return <div style={S.empty}>Nenhum lançamento registrado ainda. Use a aba "Lançamento Diário".</div>;

  const arlaList = arlaByUnit(unit);

  const s = getSaldo(last, unit);
  const cart = carteira;
  const embList = embs(unit);

  // Consumo médio real (diferença entre dias consecutivos, últimos 7 dias)
  const hist7 = lancamentos.slice(-8);
  const consumosUr = [];
  const consumosPe = [];
  for (let i = 1; i < hist7.length; i++) {
    const prev = getSaldo(hist7[i - 1], unit);
    const curr = getSaldo(hist7[i], unit);
    consumosUr.push(Math.max(0, prev.urSaldo - curr.urSaldo));
    if (hasPE(unit)) consumosPe.push(Math.max(0, (prev.peSaldo || 0) - (curr.peSaldo || 0)));
  }
  const consumoUrMedio = consumosUr.length > 0
    ? consumosUr.reduce((a, b) => a + b, 0) / consumosUr.length
    : 11000;
  const consumoPeMedio = consumosPe.length > 0
    ? consumosPe.reduce((a, b) => a + b, 0) / consumosPe.length
    : 738;

  // Projeção de ruptura baseada no consumo real
  const diasUrReal = consumoUrMedio > 0 ? Math.round(s.urSaldo / consumoUrMedio) : 999;
  const diasPeReal = hasPE(unit) && consumoPeMedio > 0 ? Math.round((s.peSaldo || 0) / consumoPeMedio) : null;

  // Alertas
  const alerts = [];
  const gDef = (cart.granel || 0) - (s.arla.granel || 0);
  if (gDef > 0) alerts.push({ t: 'crit', m: `Déficit granel: ${fmt(gDef)} L a produzir para atender a carteira` });
  if (diasUrReal <= 3) alerts.push({ t: 'crit', m: `Ureia acaba em ${diasUrReal} dia${diasUrReal === 1 ? '' : 's'} com base no consumo real!` });
  else if (diasUrReal <= 10) alerts.push({ t: 'warn', m: `Ureia acaba em ${diasUrReal} dias com base no consumo real` });
  if (diasPeReal !== null && diasPeReal <= 3) alerts.push({ t: 'crit', m: `Polietileno acaba em ${diasPeReal} dia${diasPeReal === 1 ? '' : 's'}!` });
  else if (diasPeReal !== null && diasPeReal <= 10) alerts.push({ t: 'warn', m: `Polietileno acaba em ${diasPeReal} dias` });
  if (s.urSaldo < 50000 && diasUrReal > 10) alerts.push({ t: 'warn', m: `Ureia baixa: ${fmt(s.urSaldo)} kg em estoque` });
  if (hasPE(unit) && (s.peSaldo || 0) < 20000 && diasPeReal !== null && diasPeReal > 10) alerts.push({ t: 'warn', m: `Polietileno baixo: ${fmt(s.peSaldo)} kg` });
  if (!alerts.length) alerts.push({ t: 'ok', m: 'Todos os estoques em nível adequado' });

  // Histórico para gráficos
  const hist = lancamentos.slice(-30);
  const histLabels = hist.map(d => {
    const dt = new Date(d.data + 'T12:00:00');
    return `${dt.getDate()}/${dt.getMonth() + 1}`;
  });
  const urHist     = hist.map(d => getSaldo(d, unit).urSaldo);
  const granelHist = hist.map(d => getSaldo(d, unit).arla.granel || 0);
  const bomb20Hist = hist.map(d => getSaldo(d, unit).arla.bomb20 || 0);
  const consumoUrHist = hist.map((d, i) => {
    if (i === 0) return 0;
    return Math.max(0, getSaldo(hist[i - 1], unit).urSaldo - getSaldo(d, unit).urSaldo);
  }).slice(1);
  const consumoPeHist = hasPE(unit) ? hist.map((d, i) => {
    if (i === 0) return 0;
    return Math.max(0, (getSaldo(hist[i - 1], unit).peSaldo || 0) - (getSaldo(d, unit).peSaldo || 0));
  }).slice(1) : [];
  const chartLabels = histLabels.slice(1);

  // Faturamento acumulado mensal e anual (soma das saídas por produto)
  const hoje = new Date();
  const fatMes = {};
  const fatAno = {};
  ARLA.forEach(p => { fatMes[p.id] = 0; fatAno[p.id] = 0; });
  lancamentos.forEach(d => {
    const dt = new Date(d.data + 'T12:00:00');
    const noMes = dt.getMonth() === hoje.getMonth() && dt.getFullYear() === hoje.getFullYear();
    const noAno = dt.getFullYear() === hoje.getFullYear();
    ARLA.forEach(p => {
      const saida = d.arla?.[p.id]?.saida || 0;
      if (noMes) fatMes[p.id] += saida;
      if (noAno) fatAno[p.id] += saida;
    });
  });

  // Taxa de atendimento da carteira
  const taxas = CART_IDS.map(id => {
    const ct = cart[id] || 0;
    if (!ct) return null;
    const saldo = s.arla[id] || 0;
    const pct = Math.min(100, Math.round((saldo / ct) * 100));
    const cor = pct >= 100 ? '#1a5c38' : pct >= 50 ? '#d97706' : '#dc2626';
    const prod = ARLA.find(p => p.id === id);
    return { id, label: prod?.label || id, pct, cor };
  }).filter(Boolean);

  const AutoBar = ({ dias, label, consumoMedio }) => {
    const pct = Math.min(100, dias / 90 * 100);
    const [cor, bgc, tc, lbl] = dias > 30 ? ['#2d7a47','#eaf3de','#1a5c38','OK']
      : dias > 10 ? ['#d97706','#fef3c7','#92400e','Atenção']
      : ['#dc2626','#fee2e2','#991b1b','Crítico'];
    return (
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
          <span style={S.barLabel}>{label}</span>
          <span style={{ fontSize:11, color:'#aaa', fontFamily:'"DM Sans", sans-serif' }}>média: {fmt(Math.round(consumoMedio))} kg/dia</span>
        </div>
        <div style={S.barRow}>
          <span style={S.barDias}>{dias} dias</span>
          <div style={S.barBg}><div style={{ ...S.barFill, width:`${pct}%`, background:cor }} /></div>
          <span style={{ ...S.barTag, background:bgc, color:tc }}>{lbl}</span>
        </div>
      </div>
    );
  };

  return (
    <div>
      {alerts.map((a, i) => (
        <div key={i} style={{ ...S.alert, ...(a.t==='crit'?S.alertCrit:a.t==='warn'?S.alertWarn:S.alertOk) }}>{a.m}</div>
      ))}

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:hasPE(unit)?`repeat(${hasAditivo(unit)?5:4},1fr)`:'repeat(3,1fr)', gap:10, marginBottom:14 }}>
        {hasPE(unit) && (
          <div style={S.kpi}>
            <div style={S.kpiLabel}>Polietileno</div>
            <div style={S.kpiValue}>{fmt(s.peSaldo)} <span style={S.kpiUnit}>kg</span></div>
            <div style={S.kpiSub}>Ruptura em ~{diasPeReal} dias</div>
          </div>
        )}
        {hasAditivo(unit) && (
          <div style={S.kpi}>
            <div style={S.kpiLabel}>Aditivo</div>
            <div style={S.kpiValue}>{fmt(s.aditivoSaldo)} <span style={S.kpiUnit}>L</span></div>
            <div style={S.kpiSub}>Matéria prima</div>
          </div>
        )}
        <div style={S.kpi}>
          <div style={S.kpiLabel}>Ureia</div>
          <div style={S.kpiValue}>{fmt(s.urSaldo)} <span style={S.kpiUnit}>kg</span></div>
          <div style={S.kpiSub}>Ruptura em ~{diasUrReal} dias</div>
        </div>
        <div style={S.kpi}>
          <div style={S.kpiLabel}>Bombona 20L Arla</div>
          <div style={S.kpiValue}>{fmt(s.arla.bomb20)} <span style={S.kpiUnit}>un</span></div>
          <div style={S.kpiSub}>Carteira: {fmt(cart.bomb20)} un</div>
        </div>
        <div style={S.kpi}>
          <div style={S.kpiLabel}>Granel Arla</div>
          <div style={S.kpiValue}>{fmt(s.arla.granel)} <span style={S.kpiUnit}>L</span></div>
          <div style={S.kpiSub}>Carteira: {fmt(cart.granel)} L</div>
        </div>
      </div>

      {/* Taxa de atendimento */}
      {taxas.length > 0 && (
        <div style={{ ...S.card, marginBottom:14 }}>
          <div style={S.cardTitle}>Taxa de atendimento da carteira</div>
          <div style={{ display:'flex', gap:40, justifyContent:'flex-start', flexWrap:'wrap', padding:'8px 0' }}>
            {taxas.map(t => <Gauge key={t.id} pct={t.pct} label={t.label} color={t.cor} />)}
          </div>
        </div>
      )}

      {/* Gráficos de tendência */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
        <div style={S.card}>
          <div style={S.cardTitle}>Evolução — Ureia total (kg)</div>
          {urHist.length >= 2
            ? <LineChart series={[urHist]} colors={['#185FA5']} labels={histLabels} height={90} />
            : <div style={S.chartEmpty}>Continue lançando diariamente para ver a tendência</div>}
          <div style={S.chartFooter}>
            <span>Mín: {fmt(Math.min(...urHist))}</span>
            <span>Máx: {fmt(Math.max(...urHist))}</span>
            <span>Atual: {fmt(urHist[urHist.length-1])}</span>
          </div>
        </div>

        <div style={S.card}>
          <div style={S.cardTitle}>Evolução — Granel e Bombona 20L</div>
          {granelHist.length >= 2
            ? <LineChart series={[granelHist, bomb20Hist]} colors={['#1a5c38','#185FA5']} labels={histLabels} height={90} />
            : <div style={S.chartEmpty}>Continue lançando diariamente para ver a tendência</div>}
          <div style={S.chartFooter}>
            <span style={{ color:'#1a5c38' }}>— Granel</span>
            <span style={{ color:'#185FA5' }}>— Bombona 20L</span>
          </div>
        </div>

        <div style={S.card}>
          <div style={S.cardTitle}>Consumo diário — Ureia (kg)</div>
          {consumoUrHist.length >= 2
            ? <BarChart data={consumoUrHist} color="#185FA5" labels={chartLabels} height={90} />
            : <div style={S.chartEmpty}>Dados insuficientes</div>}
          <div style={S.chartFooter}>
            <span>Média: {fmt(Math.round(consumoUrMedio))} kg/dia</span>
            <span>Total período: {fmt(consumoUrHist.reduce((a,b)=>a+b,0))} kg</span>
          </div>
        </div>

        {hasPE(unit) ? (
          <div style={S.card}>
            <div style={S.cardTitle}>Consumo diário — Polietileno (kg)</div>
            {consumoPeHist.length >= 2
              ? <BarChart data={consumoPeHist} color="#7c3aed" labels={chartLabels} height={90} />
              : <div style={S.chartEmpty}>Dados insuficientes</div>}
            <div style={S.chartFooter}>
              <span>Média: {fmt(Math.round(consumoPeMedio))} kg/dia</span>
            </div>
          </div>
        ) : (
          <div style={S.card}>
            <div style={S.cardTitle}>Autonomia de matéria prima</div>
            <div style={{ paddingTop: 8 }}>
              <AutoBar dias={diasUrReal} label="Ureia" consumoMedio={consumoUrMedio} />
            </div>
          </div>
        )}
      </div>

      {/* Faturamento acumulado */}
      <div style={{ ...S.card, marginBottom:14 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <div style={S.cardTitle} >Faturamento acumulado</div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:10 }}>
          {arlaList.filter(p => CART_IDS.includes(p.id)).map(p => (
            <div key={p.id} style={{ background:'#f5f4f0', borderRadius:8, padding:'10px 12px' }}>
              <div style={{ fontSize:11, color:'#888', marginBottom:6, fontFamily:'"DM Sans", sans-serif', fontWeight:500 }}>{p.label}</div>
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                  <span style={{ fontSize:10, color:'#aaa', fontFamily:'"DM Sans", sans-serif' }}>Mensal</span>
                  <span style={{ fontSize:14, fontWeight:600, color:'#1a1a18', fontFamily:'"DM Sans", sans-serif' }}>
                    {fmt(fatMes[p.id])} <span style={{ fontSize:10, color:'#aaa', fontWeight:400 }}>{p.unit}</span>
                  </span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                  <span style={{ fontSize:10, color:'#aaa', fontFamily:'"DM Sans", sans-serif' }}>Anual</span>
                  <span style={{ fontSize:14, fontWeight:600, color:'#185FA5', fontFamily:'"DM Sans", sans-serif' }}>
                    {fmt(fatAno[p.id])} <span style={{ fontSize:10, color:'#aaa', fontWeight:400 }}>{p.unit}</span>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MP + Embalagens */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
        <div style={S.card}>
          <div style={S.cardTitle}>Matéria prima — Ureia por fornecedor</div>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Fornecedor</th><th style={{...S.th,textAlign:'right'}}>Saldo (kg)</th></tr></thead>
            <tbody>
              {FORNECEDORES.map(f => (
                <tr key={f.id}><td style={S.td}>{f.label}</td><td style={{...S.td,textAlign:'right'}}>{fmt(s.ureiaSaldos?.[f.id]||0)}</td></tr>
              ))}
              <tr><td style={{...S.td,fontWeight:600}}>Total</td><td style={{...S.td,textAlign:'right',fontWeight:600}}>{fmt(s.urSaldo)}</td></tr>
            </tbody>
          </table>
          {hasPE(unit) && (
            <div style={{ marginTop:14, paddingTop:14, borderTop:'0.5px solid #e8e6e0' }}>
              <div style={{...S.cardTitle, marginBottom:8}}>Polietileno</div>
              <table style={S.table}>
                <thead><tr><th style={S.th}>Fornecedor</th><th style={{...S.th,textAlign:'right'}}>Saldo (kg)</th></tr></thead>
                <tbody><tr><td style={S.td}>Importados</td><td style={{...S.td,textAlign:'right'}}>{fmt(s.peSaldo)}</td></tr></tbody>
              </table>
            </div>
          )}
          {hasAditivo(unit) && (
            <div style={{ marginTop:14, paddingTop:14, borderTop:'0.5px solid #e8e6e0' }}>
              <div style={{...S.cardTitle, marginBottom:8}}>Aditivo</div>
              <table style={S.table}>
                <thead><tr><th style={S.th}>Descrição</th><th style={{...S.th,textAlign:'right'}}>Saldo (L)</th></tr></thead>
                <tbody><tr><td style={S.td}>Estoque</td><td style={{...S.td,textAlign:'right'}}>{fmt(s.aditivoSaldo)}</td></tr></tbody>
              </table>
            </div>
          )}
        </div>
        <div style={S.card}>
          <div style={S.cardTitle}>Embalagens</div>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Embalagem</th>
              <th style={{...S.th,textAlign:'right'}}>Saldo</th>
              <th style={S.th}>Origem</th>
              <th style={{...S.th,textAlign:'right'}}>Nec. / Excesso</th>
            </tr></thead>
            <tbody>
              {embList.map(e => {
                const saldo = s.emb[e.id]||0;
                const arlaId = EMB_ARLA_MAP[e.id];
                let nec = null;
                if (arlaId) {
                  // IBC: carteira em litros → converter para unidades (÷1000)
                  const arlaEmUnidades = arlaId === 'ibc1000'
                    ? Math.ceil(((s.arla[arlaId]||0) + (cart[arlaId]||0)) / 1000)
                    : (s.arla[arlaId]||0) + (cart[arlaId]||0);
                  const diff = saldo - arlaEmUnidades;
                  nec = diff < 0
                    ? <span style={{color:'#b91c1c',fontWeight:500}}>{fmt(Math.abs(diff))} faltam</span>
                    : <span style={{color:'#1a5c38',fontWeight:500}}>–{fmt(diff)} exc.</span>;
                }
                return (
                  <tr key={e.id}>
                    <td style={S.td}>{e.label}</td>
                    <td style={{...S.td,textAlign:'right'}}>{fmt(saldo)}</td>
                    <td style={S.td}><span style={e.produz?S.badgeOk:S.badgeGray}>{e.produz?'Produção':'Compra'}</span></td>
                    <td style={{...S.td,textAlign:'right'}}>{nec}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Arla */}
      <div style={{...S.card, marginBottom:14}}>
        <div style={S.cardTitle}>Arla — Estoque e carteira</div>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Produto</th>
            <th style={{...S.th,textAlign:'right'}}>Saldo</th>
            <th style={{...S.th,textAlign:'right'}}>Carteira</th>
            <th style={{...S.th,textAlign:'right'}}>Fat. mensal</th>
            <th style={{...S.th,textAlign:'right'}}>Fat. anual</th>
            <th style={{...S.th,textAlign:'right'}}>Saldo a prod.</th>
            <th style={S.th}>Status</th>
          </tr></thead>
          <tbody>
            {arlaList.map(p => {
              const saldo = s.arla[p.id]||0;
              const ct = cart[p.id]||0;
              const aProd = ct - saldo;
              return (
                <tr key={p.id}>
                  <td style={S.td}>{p.label} <span style={S.unit}>{p.unit}</span></td>
                  <td style={{...S.td,textAlign:'right'}}>{fmt(saldo)}</td>
                  <td style={{...S.td,textAlign:'right'}}>{fmt(ct)}</td>
                  <td style={{...S.td,textAlign:'right'}}>{fmt(fatMes[p.id]||0)}</td>
                  <td style={{...S.td,textAlign:'right',color:'#185FA5'}}>{fmt(fatAno[p.id]||0)}</td>
                  <td style={{...S.td,textAlign:'right',color:aProd>0?'#b91c1c':'#1a5c38',fontWeight:500}}>
                    {aProd>0?fmt(aProd):`–${fmt(Math.abs(aProd))}`}
                  </td>
                  <td style={S.td}>
                    {ct>0&&(aProd>0?<span style={S.badgeCrit}>Déficit</span>:<span style={S.badgeOk}>OK</span>)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Autonomia */}
      {hasPE(unit) && (
        <div style={S.card}>
          <div style={S.cardTitle}>Autonomia — baseada no consumo real dos últimos dias</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            <AutoBar dias={diasPeReal} label="Polietileno" consumoMedio={consumoPeMedio} />
            <AutoBar dias={diasUrReal} label="Ureia" consumoMedio={consumoUrMedio} />
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  empty:     { color:'#888', fontSize:14, padding:'20px 0', fontFamily:'"DM Sans", sans-serif' },
  alert:     { padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:8, fontFamily:'"DM Sans", sans-serif' },
  alertOk:   { background:'#eaf3de', color:'#1a5c38', border:'0.5px solid #b7d9a0' },
  alertWarn: { background:'#fef3c7', color:'#92400e', border:'0.5px solid #fcd34d' },
  alertCrit: { background:'#fee2e2', color:'#991b1b', border:'0.5px solid #fca5a5' },
  kpi:       { background:'#f5f4f0', borderRadius:9, padding:'12px 14px' },
  kpiLabel:  { fontSize:11, color:'#888', marginBottom:4, fontFamily:'"DM Sans", sans-serif' },
  kpiValue:  { fontSize:20, fontWeight:600, color:'#1a1a18', fontFamily:'"DM Sans", sans-serif', lineHeight:1.1 },
  kpiUnit:   { fontSize:11, color:'#aaa', fontWeight:400 },
  kpiSub:    { fontSize:11, color:'#888', marginTop:3, fontFamily:'"DM Sans", sans-serif' },
  card:      { background:'#fff', border:'0.5px solid #e8e6e0', borderRadius:10, padding:16 },
  cardTitle: { fontSize:11, fontWeight:600, color:'#888', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12, fontFamily:'"DM Sans", sans-serif' },
  table:     { width:'100%', borderCollapse:'collapse' },
  th:        { background:'#f5f4f0', padding:'7px 10px', fontSize:11, fontWeight:500, color:'#888', borderBottom:'0.5px solid #e8e6e0', fontFamily:'"DM Sans", sans-serif', textAlign:'left' },
  td:        { padding:'7px 10px', fontSize:12, borderBottom:'0.5px solid #f0ede8', color:'#1a1a18', fontFamily:'"DM Sans", sans-serif' },
  unit:      { fontSize:10, color:'#aaa' },
  badgeOk:   { background:'#eaf3de', color:'#1a5c38', fontSize:11, padding:'2px 8px', borderRadius:5, fontWeight:500 },
  badgeCrit: { background:'#fee2e2', color:'#991b1b', fontSize:11, padding:'2px 8px', borderRadius:5, fontWeight:500 },
  badgeGray: { background:'#f0ede8', color:'#888', fontSize:11, padding:'2px 8px', borderRadius:5, fontWeight:500 },
  barLabel:  { fontSize:12, color:'#888', fontFamily:'"DM Sans", sans-serif' },
  barRow:    { display:'flex', alignItems:'center', gap:10, marginTop:4 },
  barDias:   { fontSize:13, fontWeight:500, minWidth:65, fontFamily:'"DM Sans", sans-serif', color:'#1a1a18' },
  barBg:     { flex:1, height:6, background:'#e8e6e0', borderRadius:3, overflow:'hidden' },
  barFill:   { height:'100%', borderRadius:3 },
  barTag:    { fontSize:11, padding:'2px 8px', borderRadius:4, fontWeight:500, fontFamily:'"DM Sans", sans-serif' },
  chartEmpty:  { fontSize:12, color:'#bbb', textAlign:'center', padding:'20px 0', fontFamily:'"DM Sans", sans-serif' },
  chartFooter: { display:'flex', justifyContent:'space-between', fontSize:11, color:'#aaa', marginTop:6, fontFamily:'"DM Sans", sans-serif' },
};
